import { useMemo } from 'react';

import { type CheckboxOptionProps, type RadioButtonOptionProps } from '@innovationnorway/component-library';
import { CompanyAgeCriteria, CompanySizeCriteria, CustomerSegmentTargetGroup } from 'src/api/v2';
import { useTranslation } from 'react-i18next';

const useTargetPageOptions = (
  selectedCompanyAgeOption?: string,
  selectedCompanySizeOptions?: string[],
  selectedCompanyTypeOptions?: string[],
) => {
  const { t } = useTranslation('oneWayIn');
  const companyAgeOptions: RadioButtonOptionProps[] = useMemo(
    () => [
      {
        value: CompanyAgeCriteria.RelevantForAllAges,
        text: t('companyAge.RelevantForAllAges'),
        selected: selectedCompanyAgeOption === CompanyAgeCriteria.RelevantForAllAges,
      },
      {
        value: CompanyAgeCriteria.UnderThreeYears,
        text: t('companyAge.UnderThreeYears'),
        selected: selectedCompanyAgeOption === CompanyAgeCriteria.UnderThreeYears,
      },
      {
        value: CompanyAgeCriteria.UnderFiveYears,
        text: t('companyAge.UnderFiveYears'),
        selected: selectedCompanyAgeOption === CompanyAgeCriteria.UnderFiveYears,
      },
    ],
    [selectedCompanyAgeOption, t],
  );

  const companySizeOptions: CheckboxOptionProps[] = useMemo(
    () => [
      {
        value: CompanySizeCriteria.Small,
        text: t('companySize.Small'),
        selected: selectedCompanySizeOptions?.includes(CompanySizeCriteria.Small) ?? false,
      },
      {
        value: CompanySizeCriteria.Medium,
        text: t('companySize.Medium'),
        selected: selectedCompanySizeOptions?.includes(CompanySizeCriteria.Medium) ?? false,
      },
      {
        value: CompanySizeCriteria.Large,
        text: t('companySize.Large'),
        selected: selectedCompanySizeOptions?.includes(CompanySizeCriteria.Large) ?? false,
      },
      {
        value: CompanySizeCriteria.OtherCategoriesMayApply,
        text: t('companySize.OtherCategoriesMayApply'),
        selected: selectedCompanySizeOptions?.includes('no') ?? false,
      },
    ],
    [selectedCompanySizeOptions, t],
  );

  const customerSegmentOptions: CheckboxOptionProps[] = useMemo(
    () => [
      {
        value: CustomerSegmentTargetGroup.OnlyForPublicEntities,
        text: 'Kun for offentlige aktører',
        selected:
          selectedCompanyTypeOptions?.includes(CustomerSegmentTargetGroup.OnlyForPublicEntities) ?? false,
      },
      {
        value: CustomerSegmentTargetGroup.MustOwnAgriculturalProperty,
        text: 'Må ha landbrukseiendom',
        selected:
          selectedCompanyTypeOptions?.includes(CustomerSegmentTargetGroup.MustOwnAgriculturalProperty) ??
          false,
      },
      {
        value: CustomerSegmentTargetGroup.RequiresParticipationInACluster,
        text: 'Må være en del av en klynge',
        selected:
          selectedCompanyTypeOptions?.includes(CustomerSegmentTargetGroup.RequiresParticipationInACluster) ??
          false,
      },
      {
        value: CustomerSegmentTargetGroup.OnlyForTourismIndustry,
        text: 'Må være en del av reiselivsnæringen',
        selected:
          selectedCompanyTypeOptions?.includes(CustomerSegmentTargetGroup.OnlyForTourismIndustry) ?? false,
      },
    ],
    [selectedCompanyTypeOptions],
  );

  return {
    companyAgeOptions,
    companySizeOptions,
    customerSegmentOptions,
  };
};

export default useTargetPageOptions;
